import { ENDPOINTS } from 'config/endpoints';
import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import { useBackgroundInvalidateAutomatorDelay } from 'crono-fe-common/hooks/crono-extension/gateway';
import { StrategyStepPatch } from 'crono-fe-common/types/DTO/strategyStepPatch';
import { invalidateTimelineQueries } from 'hooks/services/timeline/useSearchTimeline';

interface IApplyChangesToSequenceInstances {
  strategyId: number;
  strategyStepPatchData?: StrategyStepPatch[] | null;
  deleteStepIds?: number[] | null;
  newStepIds?: number[] | null;
  refreshAll?: boolean;
}

export default function useApplyChangesToSequenceInstances() {
  const queryClient = useQueryClient();
  const { call: invalidateAutomatorDelay } =
    useBackgroundInvalidateAutomatorDelay();

  const applyChanges = (params: IApplyChangesToSequenceInstances) => {
    const request: Request = {
      url: `${ENDPOINTS.strategy.apply}`,
      config: {
        method: 'patch',
        data: { ...params },
      },
    };
    return client(request);
  };

  return useMutation<
    Response<null>,
    CronoError,
    IApplyChangesToSequenceInstances
  >(applyChanges, {
    onSettled: () => {
      invalidateApplyChangesToSequenceInstance(
        queryClient,
        invalidateAutomatorDelay,
      );
    },
  });
}

//CARE: this is only for FE usage
export const invalidateApplyChangesToSequenceInstance = async (
  queryClient: QueryClient,
  invalidateAutomatorDelay: (
    obj: object,
  ) => Promise<Record<string, never> | null>,
) => {
  await Promise.all([
    invalidateAutomatorDelay({}),
    queryClient.invalidateQueries([ENDPOINTS.strategy.main]),
    queryClient.invalidateQueries([ENDPOINTS.prospect.main]),
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey.toString().startsWith(ENDPOINTS.prospect.search.main),
    }),
    queryClient.invalidateQueries([ENDPOINTS.task.search]),
    invalidateTimelineQueries(queryClient),
    queryClient.invalidateQueries([ENDPOINTS.task.main]),
    queryClient.invalidateQueries([ENDPOINTS.task.linkedin.quantity]),
    queryClient.invalidateQueries([ENDPOINTS.applySequenceChanges.allActive]),

    queryClient.invalidateQueries([ENDPOINTS.strategy.search]),
    queryClient.invalidateQueries([ENDPOINTS.strategy.prospect]),
    queryClient.invalidateQueries([ENDPOINTS.prospect.search.strategy]),
  ]);
};
